import {
    useRef
} from 'react';
import {
    Send, Square, User, ShieldAlert, Check, X, Wrench, Loader2, Paperclip, FileText,
    PanelLeft, Plus, Maximize2, Minimize2, Minus, Bug
} from 'lucide-react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
    Button
} from 'shared/components/ui/button';
import {
    Card
} from 'shared/components/ui/card';
import BexiLogo from './BexiLogo';
import {
    toolLabel
} from './bexiConstants';

/** Panel header: recents toggle, title, model picker, new chat, expand, close. */
export function PanelHeader({
    onToggleSidebar, model, setModel, models, autoApprove, onToggleAuto,
    onNewChat, isLoading, onToggleExpand, expanded, onMinimize, onClose,
    onOpenDebug, debugCount
}) {
    return (
        <div className="flex items-center justify-between border-b px-3 py-2">
            <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" onClick={onToggleSidebar} title="Recent chats">
                    <PanelLeft className="h-4 w-4" />
                </Button>
                <span className="flex items-center gap-2 font-semibold"><BexiLogo size={18} /> BEXI</span>
            </div>
            <div className="flex items-center gap-1">
                <select
                    value={model}
                    onChange={(e) => setModel(e.target.value)}
                    className="rounded border bg-background px-2 py-1 text-xs"
                >
                    {models.map((mo) => <option key={mo.value} value={mo.value}>{mo.label}</option>)}
                </select>
                <button
                    type="button"
                    onClick={onToggleAuto}
                    title={autoApprove ? 'Auto-run writes is ON — writes run without approval' : 'Auto-run is OFF — writes need approval'}
                    className={`rounded border px-2 py-1 text-[11px] font-medium ${autoApprove ? 'border-green-400 bg-green-50 text-green-700 dark:bg-green-950/40' : 'text-muted-foreground'}`}
                >
                    {autoApprove ? 'Auto-run' : 'Confirm'}
                </button>
                <Button variant="ghost" size="icon" onClick={onNewChat} title="New chat" disabled={isLoading}>
                    <Plus className="h-4 w-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={onOpenDebug} title="Debug trace">
                    <span className="relative">
                        <Bug className="h-4 w-4" />
                        {debugCount > 0 && (
                            <span className="absolute -right-1 -top-1 h-1.5 w-1.5 rounded-full bg-primary" />
                        )}
                    </span>
                </Button>
                {onToggleExpand && (
                    <Button variant="ghost" size="icon" onClick={onToggleExpand} title={expanded ? 'Collapse' : 'Expand'}>
                        {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                    </Button>
                )}
                {onMinimize && (
                    <Button variant="ghost" size="icon" onClick={onMinimize} title="Minimize — keep this chat and hide the panel">
                        <Minus className="h-4 w-4" />
                    </Button>
                )}
                {onClose && (
                    <Button variant="ghost" size="icon" onClick={onClose} title="Close and end this chat">
                        <X className="h-4 w-4" />
                    </Button>
                )}
            </div>
        </div>
    );
}

/** Empty state: BEXI logo, intro line, reshuffled starter chips. */
export function EmptyState({
    chips, onPick, intro
}) {
    return (
        <div className="flex flex-1 flex-col items-center justify-center px-6 text-center">
            <div className="mb-3">
                <BexiLogo size={48} />
            </div>
            <p className="mb-5 max-w-sm text-sm text-muted-foreground">{intro}</p>
            <div className="flex max-w-md flex-wrap justify-center gap-2">
                {chips.map((c) => (
                    <button
                        key={c}
                        type="button"
                        onClick={() => onPick(c)}
                        className="rounded-full border px-3 py-1.5 text-xs text-foreground hover:bg-muted"
                    >
                        {c}
                    </button>
                ))}
            </div>
        </div>
    );
}

/** Auto-growing composer with attachments, Send/Stop and the privacy line. */
export function Composer({
    taRef, value, onChange, onKeyDown, onSend, onStop, isLoading, tall, expanded,
    attachments = [], onAddFiles, onRemoveAttachment,
}) {
    const fileRef = useRef(null);
    const canAttach = attachments.length < 3;
    // Paste an image (or PDF) straight into the composer → attach it.
    const onPaste = (e) => {
        const items = e.clipboardData && e.clipboardData.items;
        if (!items) return;
        const files = [];
        for (const it of items) {
            if (it.kind === 'file' && (it.type.startsWith('image/') || it.type === 'application/pdf')) {
                const f = it.getAsFile();
                if (f) files.push(f);
            }
        }
        if (files.length) { e.preventDefault(); onAddFiles(files); }
    };
    return (
        <div className="border-t p-3">
            <div className={`mx-auto ${expanded ? 'max-w-3xl' : ''}`}>
                {attachments.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-2">
                        {attachments.map((a, i) => (
                            <div key={i} className="relative flex items-center gap-1 rounded border bg-muted px-2 py-1 text-xs">
                                {a.reading ? (
                                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                                ) : a.type === 'image' ? (
                                    <img src={a.dataUrl} alt={a.name} className="h-8 w-8 rounded object-cover" />
                                ) : (
                                    <FileText className="h-4 w-4" />
                                )}
                                <span className="max-w-[120px] truncate">{a.name}</span>
                                <button type="button" onClick={() => onRemoveAttachment(i)} title="Remove">
                                    <X className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <div className="flex items-end gap-2">
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*,application/pdf"
                        multiple
                        className="hidden"
                        onChange={(e) => { onAddFiles(e.target.files); e.target.value = ''; }}
                    />
                    <Button
                        variant="outline"
                        size="icon"
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={!canAttach || isLoading}
                        title={canAttach ? 'Attach images or PDF (max 3, 5MB)' : 'Attachment limit reached'}
                    >
                        <Paperclip className="h-4 w-4" />
                    </Button>
                    <textarea
                        ref={taRef}
                        value={value}
                        onChange={(e) => onChange(e.target.value)}
                        onKeyDown={onKeyDown}
                        onPaste={onPaste}
                        placeholder="Ask BEXI to build or change something…"
                        rows={tall ? 3 : 1}
                        className="flex-1 resize-none rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    {isLoading ? (
                        <Button onClick={onStop} size="icon" variant="destructive" title="Stop">
                            <Square className="h-4 w-4" />
                        </Button>
                    ) : (
                        <Button onClick={onSend} size="icon" disabled={!value.trim() && attachments.length === 0} title="Send">
                            <Send className="h-4 w-4" />
                        </Button>
                    )}
                </div>
                <div className="mt-1 text-[10.5px] text-muted-foreground">
                    Enter to send · Shift+Enter for a new line. Images/PDF supported. BEXI acts only in this organization and asks before it writes.
                </div>
            </div>
        </div>
    );
}

/** A single message: user bubble, tool output, or streamed assistant markdown. */
export function MessageBubble({
    m
}) {
    const isUser = m.role === 'user';
    const isTool = m.role === 'tool';

    if (isUser) {
        return (
            <div className="flex justify-end gap-2">
                <div className="max-w-[80%] rounded-lg bg-muted px-3 py-2 text-sm">
                    {(m.attachments || []).length > 0 && (
                        <div className="mb-1 flex flex-wrap gap-1.5">
                            {m.attachments.map((a, i) => (a.type === 'image' ? (
                                <img key={i} src={a.dataUrl} alt={a.name} className="h-16 w-16 rounded object-cover" />
                            ) : (
                                <span key={i} className="flex items-center gap-1 rounded border bg-background px-1.5 py-0.5 text-xs">
                                    <FileText className="h-3.5 w-3.5" /> {a.name}
                                </span>
                            )))}
                        </div>
                    )}
                    <div className="whitespace-pre-wrap">{m.content}</div>
                </div>
                <div className="mt-1 shrink-0 text-muted-foreground"><User className="h-4 w-4" /></div>
            </div>
        );
    }

    return (
        <div className="flex gap-2">
            <div className="mt-1 shrink-0 text-muted-foreground">
                {isTool ? <Wrench className="h-4 w-4" /> : <BexiLogo size={18} />}
            </div>
            <div className="min-w-0 flex-1">
                {isTool ? (
                    <div className="rounded-lg bg-muted px-3 py-2 font-mono text-xs">
                        {m.content}
                        {m.result !== undefined && (
                            <pre className="mt-1 overflow-x-auto text-[11px] opacity-80">{JSON.stringify(m.result, null, 2)}</pre>
                        )}
                    </div>
                ) : (
                    <>
                        {/* Chronological timeline: text blocks and tool rows in the
                            order they streamed. History (no parts) falls back to content. */}
                        {(m.parts || []).length > 0 ? (
                            <div className="space-y-1">
                                {m.parts.map((p, i) => (p.kind === 'text' ? (
                                    p.text && (
                                        <div key={i} className="agent-md max-w-none text-sm">
                                            <Markdown remarkPlugins={[remarkGfm]}>{p.text}</Markdown>
                                        </div>
                                    )
                                ) : (
                                    <div key={i} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                                        {p.status === 'done'
                                            ? <Check className="h-3 w-3 shrink-0 text-green-600" />
                                            : <Loader2 className="h-3 w-3 shrink-0 animate-spin" />}
                                        <Wrench className="h-2.5 w-2.5 shrink-0" />
                                        <span className="truncate">
                                            {toolLabel(p.name)}
                                            {p.detail ? <span className="opacity-70"> — {p.detail}</span> : null}
                                        </span>
                                    </div>
                                )))}
                            </div>
                        ) : (
                            <div className="agent-md max-w-none text-sm">
                                <Markdown remarkPlugins={[remarkGfm]}>{m.content || ''}</Markdown>
                            </div>
                        )}
                        {m.streaming && m.status && (
                            <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                                <Loader2 className="h-3.5 w-3.5 animate-spin" /> {m.status}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}

/** Approve/reject card for a proposed write (create/delete-scope tool). */
export function WriteCard({
    w, disabled, onApprove, onReject
}) {
    const settled = w.done || w.rejected;
    return (
        <Card className="mt-2 border-amber-300 bg-amber-50 p-3 dark:bg-amber-950/30">
            <div className="flex items-center gap-2 text-sm font-semibold text-amber-700 dark:text-amber-400">
                <ShieldAlert className="h-4 w-4" />
                Approve write: <code className="font-mono">{w.tool}</code>
                <span className="rounded bg-amber-200 px-1 text-[10px] uppercase text-amber-800 dark:bg-amber-800 dark:text-amber-100">{w.scope}</span>
            </div>
            <pre className="mt-2 max-h-40 overflow-auto rounded bg-background/60 p-2 text-[11px]">{JSON.stringify(w.args, null, 2)}</pre>
            {settled ? (
                <div className="mt-2 text-xs font-medium">
                    {w.done ? (w.ok ? '✓ Approved and ran' : '✗ Ran with error') : '✗ Rejected'}
                </div>
            ) : (
                <div className="mt-2 flex gap-2">
                    <Button size="sm" disabled={disabled} onClick={onApprove}>
                        <Check className="mr-1 h-4 w-4" /> Approve &amp; run
                    </Button>
                    <Button size="sm" variant="outline" disabled={disabled} onClick={onReject}>
                        <X className="mr-1 h-4 w-4" /> Reject
                    </Button>
                </div>
            )}
        </Card>
    );
}
